import { Button, Checkbox, FormGroup, Input, Label, Select } from '@/components'
import { TInput } from '@/components/forms/form-elements/types'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useRedis } from '@/hooks/useRedis'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import commonUtils from '@/utils/commonUtils'
import { useDialog } from '@/context/dialogContext'

interface DataRow {
  cmpnyCd: string
  chnlId: string
  regId: string
  regDate: string
  updId: string
  updDate: string
  chnlNm: string
  chnlDesc: string
  useYn: string
  useYnNm: string
  applyDate: string
  title: string
  customUrl: string
  publishedAt: string
  description: string
  thumbnailsDefault: string
  thumbnailsHigh: string
  thumbnailsMedium: string
  subscriberCount: string
  videoCount: string
  viewCount: string
  hiddenSubscriberCountYn: string
  likesCount: string
  checked: boolean
}

const Youtube: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert, showConfirm } = useDialog()

  const [data, setData] = useState<DataRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copyModalOpen, setCopyModalOpen] = useState(false)
  const [copyModalData, setCopyModalData] = useState<DataRow[]>([])
  const tableRef = useRef<HTMLTableElement>(null)

  const [searchData, setSearchData] = useState(
    location.state?.searchData || {
      useYn: '',
      type: '',
      search: '',
    },
  )

  const { getComCodes, getComCodeValue } = useRedis()

  // 이미지 컴포넌트 최적화
  const LazyImage = React.memo(
    ({ src, alt, index }: { src: string; alt: string; index: number }) => {
      const [show, setShow] = useState(false)
      const [retry, setRetry] = useState(0)
      const [imageError, setImageError] = useState(false)

      useEffect(() => {
        // 스태거링 로드로 브라우저 부하 줄이기
        const timeout = setTimeout(() => setShow(true), index * 100)
        return () => clearTimeout(timeout)
      }, [index])

      // 이미지 로드 실패 시 재시도
      useEffect(() => {
        if (retry < 3 && imageError) {
          const timeout = setTimeout(() => {
            setShow(true)
            setImageError(false)
          }, 500)
          return () => clearTimeout(timeout)
        }
      }, [retry, imageError])

      if (retry >= 3) {
        return (
          <div
            style={{
              width: '30px',
              height: '30px',
              backgroundColor: '#f0f0f0',
            }}
          />
        )
      }

      return (
        <img
          src={show ? src : ''}
          alt={alt}
          style={{ width: '30px', height: '30px', objectFit: 'cover' }}
          onError={() => {
            setImageError(true)
            setShow(false)
            setRetry((prev) => prev + 1)
          }}
          loading="lazy"
        />
      )
    },
  )
  LazyImage.displayName = 'LazyImage'

  const handleCheckBoxChange = useCallback(
    (e: React.ChangeEvent<TInput> | any) => {
      const { name, id } = e.target
      if ('allCheck' === name) {
        setData((prevData) =>
          prevData.map((item) => ({ ...item, checked: e.target.checked })),
        )
      } else {
        setData((prevData) =>
          prevData.map((item) =>
            item.chnlId === id ? { ...item, checked: e.target.checked } : item,
          ),
        )
      }
    },
    [],
  )

  const columns: TableColumn<DataRow>[] = [
    {
      name: (
        <Checkbox
          label={''}
          name={'allCheck'}
          id={'allCheck'}
          onChange={handleCheckBoxChange}
        />
      ),
      cell: (row) => (
        <Checkbox
          label={''}
          id={`${row.chnlId}`}
          name={'check'}
          checked={row.checked}
          onChange={handleCheckBoxChange}
        />
      ),
      width: '5%',
    },
    {
      name: '로고',
      cell: (row, index) =>
        row.thumbnailsDefault ? (
          <LazyImage
            src={row.thumbnailsDefault}
            alt="기본이미지"
            index={index || 0}
          />
        ) : (
          <div
            style={{
              width: '30px',
              height: '30px',
              backgroundColor: '#f0f0f0',
            }}
          />
        ),
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '채널명',
      selector: (row) => row.title || row.chnlNm,
      center: true,
      wrap: true,
      width: '20%',
    },
    {
      name: '구독자수',
      selector: (row) =>
        row.subscriberCount ? row.subscriberCount.toLocaleString() + '명' : '',
      center: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '설명',
      selector: (row) => row.chnlDesc,
      center: true,
      wrap: true,
      width: '25%',
    },
    {
      name: '사용여부',
      selector: (row) => row.useYnNm || row.useYn,
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '적용일시',
      selector: (row) => commonUtils.formatDate(row.applyDate, false),
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '좋아요',
      selector: (row) => row.likesCount || 0,
      center: true,
      wrap: true,
      width: '5%',
    },
    {
      name: '',
      cell: (row) => (
        <Button
          variant="outlined"
          color="neverbe"
          size="xs"
          onClick={() => handleButtonDetailClick(row)}
        >
          상세
        </Button>
      ),
      sortable: false,
      width: '10%',
    },
  ]

  const handleButtonDetailClick = useCallback(
    async (data: DataRow) => {
      if (data.applyDate && data.applyDate.length == 8) {
        navigate(`/youtube/detail`, {
          state: {
            chnlId: data.chnlId,
            searchData: searchData,
          },
        })
      } else {
        navigate('/youtube/insert', { state: { data: data } })
      }
    },
    [navigate, searchData],
  )

  const handleSearch = useCallback((e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement
    setSearchData((prevState: any) => ({
      ...prevState,
      [name]: value,
    }))
  }, [])

  // API 호출 함수 개선
  const getYoutubeList = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // AbortController로 요청 취소 가능하게 설정
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30초 타임아웃

      const response = await axios.post('/youtube/list', searchData, {
        signal: controller.signal,
        timeout: 30000, // axios 타임아웃도 설정
      })

      clearTimeout(timeoutId)

      const listData: DataRow[] = response.data.data.list
      if (Array.isArray(listData)) {
        for (const data of listData) {
          data.useYnNm = getComCodeValue('USE_YN', data.useYn)?.codeNm || ''
        }
        setData(listData)
      }
    } catch (err: any) {
      console.error('YouTube 리스트 조회 실패:', err)

      if (err.name === 'AbortError') {
        setError('요청 시간이 초과되었습니다. 다시 시도해주세요.')
      } else if (err.code === 'NETWORK_ERROR') {
        setError('네트워크 연결을 확인해주세요.')
      } else {
        setError('데이터 조회 중 오류가 발생했습니다.')
      }

      showAlert({
        type: 'error',
        title: '오류',
        message: error || '데이터 조회에 실패했습니다.',
        onClose() {
          setError(null)
        },
      })
    } finally {
      setLoading(false)
    }
  }, [axios, searchData, getComCodeValue, showAlert, error])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !loading) {
        getYoutubeList()
      }
    },
    [getYoutubeList, loading],
  )

  // API 호출 함수도 개선
  const handleApiCall = useCallback(async () => {
    const checkedList = data.filter((item) => item.checked === true)

    if (checkedList.length === 0) {
      showAlert({
        type: 'warning',
        title: '유튜브를 선택해주세요.',
        onClose() {},
      })
      return
    }

    try {
      setLoading(true)
      const response: any = await axios.post(
        '/youtube/getChannelListApi',
        { channels: checkedList },
        { timeout: 60000 }, // API 호출은 더 긴 타임아웃
      )

      if (response.status === 200) {
        showAlert({
          type: 'success',
          title: 'API 호출이 완료되었습니다.',
          onClose() {},
        })
        await getYoutubeList()
      }
    } catch (err: any) {
      console.error('API 호출 실패:', err)
      showAlert({
        type: 'error',
        title: 'API 호출 중 오류가 발생했습니다.',
        message: err.message || '다시 시도해주세요.',
        onClose() {},
      })
    } finally {
      setLoading(false)
    }
  }, [data, axios, showAlert, getYoutubeList])

  useEffect(() => {
    getYoutubeList()
  }, [])

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>유튜브관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <Select
                id="useYn"
                name="useYn"
                width="100px"
                onChange={handleSearch}
                value={searchData.useYn}
                disabled={loading}
              >
                <option value="">전체</option>
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'type'}>검색종류</Label>
              <Select
                id="type"
                name="type"
                width="100px"
                onChange={handleSearch}
                value={searchData.type}
                disabled={loading}
              >
                <option value="">전체</option>
                <option value="01">채널명</option>
                <option value="02">설명</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'search'}>
                <></>
              </Label>
              <FlexWrapper>
                <Input
                  placeholder=""
                  width="300px"
                  id="search"
                  name="search"
                  maxLength={100}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={getYoutubeList}
                  color="neverbe"
                  variant="outlined"
                  disabled={loading}
                >
                  {loading ? '검색 중...' : '검색'}
                </Button>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={() => navigate('/youtube/insert', {})}
                  color="neverbe"
                  disabled={loading}
                >
                  등록
                </Button>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={handleApiCall}
                  color="neverbe"
                  disabled={loading}
                >
                  {loading ? 'API 호출 중...' : 'API호출'}
                </Button>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={() => {
                    if (data.length > 0) {
                      setCopyModalData(data);
                      setCopyModalOpen(true);
                    } else {
                      showAlert({
                        type: 'warning',
                        title: '조회된 데이터가 없습니다.',
                        onClose() {},
                      });
                    }
                  }}
                  color="neverbe"
                  variant="outlined"
                >
                  복사
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            color: 'red',
            marginBottom: '10px',
            padding: '10px',
            backgroundColor: '#ffeaea',
          }}
        >
          {error}
        </div>
      )}

      {copyModalOpen && (
        <>
          <div
            onClick={() => setCopyModalOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(15,15,35,0.55)',
              backdropFilter: 'blur(2px)',
              zIndex: 999,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#ffffff',
              width: '860px',
              maxHeight: '82vh',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* 헤더 */}
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '18px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ color: '#fff', fontSize: '18px', fontWeight: 700, letterSpacing: '-0.3px' }}>
                  📺 채널 정보
                </div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '2px' }}>
                  총 {copyModalData.length}개 채널
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => {
                    const headers = ['순위', '채널명', '구독자수', '설명', '유튜브 링크'];
                    const rows = copyModalData.map((row, idx) => ({
                      rank: String(idx + 1),
                      name: row.title || row.chnlNm,
                      subscribers: row.subscriberCount ? row.subscriberCount.toLocaleString() + '명' : '',
                      desc: row.chnlDesc,
                      url: row.customUrl ? `https://www.youtube.com/${row.customUrl}` : '',
                    }));

                    const thStyle = 'border:1px solid #c7b8e8;padding:10px 14px;text-align:center;font-weight:700;font-size:13px;color:#ffffff;background-color:#7c6bb0;white-space:nowrap;';
                    const tdBase = 'border:1px solid #e5e7eb;padding:10px 14px;font-size:13px;';
                    const tdCenter = tdBase + 'text-align:center;white-space:nowrap;';
                    const tdDesc = tdBase + 'text-align:left;line-height:1.6;color:#374151;';
                    const aStyle = 'color:#667eea;font-weight:600;text-decoration:none;';

                    const htmlTable = `<table style="border-collapse:collapse;width:100%;font-size:13px;font-family:'Apple SD Gothic Neo','Malgun Gothic',sans-serif;">
                      <thead><tr>${headers.map((h) => `<th style="${thStyle}">${h}</th>`).join('')}</tr></thead>
                      <tbody>${rows.map((r, i) => `<tr style="background-color:${i % 2 === 0 ? '#ffffff' : '#f5f3ff'};">
                        <td style="${tdCenter}font-weight:700;color:#7c6bb0;">${r.rank}</td>
                        <td style="${tdCenter}font-weight:600;color:#111827;">${r.name}</td>
                        <td style="${tdCenter}font-weight:600;color:#059669;">${r.subscribers}</td>
                        <td style="${tdDesc}">${r.desc}</td>
                        <td style="${tdCenter}">${r.url ? `<a href="${r.url}" target="_blank" style="${aStyle}">${r.url}</a>` : ''}</td>
                      </tr>`).join('')}</tbody>
                    </table>`;

                    const plainText = [headers, ...rows.map((r) => [r.rank, r.name, r.subscribers, r.desc, r.url])].map((r) => (r as string[]).join('\t')).join('\n');

                    const clipItem = new ClipboardItem({
                      'text/html': new Blob([htmlTable], { type: 'text/html' }),
                      'text/plain': new Blob([plainText], { type: 'text/plain' }),
                    });
                    navigator.clipboard.write([clipItem]).then(() => {
                      showAlert({ type: 'success', title: '복사되었습니다.', onClose() {} });
                    });
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 600,
                    padding: '7px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.35)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                >
                  📋 표 복사
                </button>
                <button
                  onClick={() => setCopyModalOpen(false)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '16px',
                    width: '34px',
                    height: '34px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* 테이블 */}
            <div style={{ overflowY: 'auto', padding: '20px 24px 24px' }}>
              <table
                ref={tableRef}
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}
              >
                <thead>
                  <tr>
                    {[
                      { label: '순위', width: '6%' },
                      { label: '채널명', width: '20%' },
                      { label: '구독자수', width: '11%' },
                      { label: '설명', width: '33%' },
                      { label: '유튜브 링크', width: '30%' },
                    ].map((h) => (
                      <th
                        key={h.label}
                        style={{
                          width: h.width,
                          padding: '11px 14px',
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: '12px',
                          color: '#6b7280',
                          backgroundColor: '#f9fafb',
                          borderBottom: '2px solid #e5e7eb',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {copyModalData.map((row, idx) => (
                    <tr
                      key={row.chnlId}
                      style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f4ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#fafafa')}
                    >
                      <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          background: idx < 3 ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#f3f4f6',
                          color: idx < 3 ? '#fff' : '#6b7280',
                          fontWeight: 700,
                          fontSize: '12px',
                        }}>
                          {idx + 1}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#111827', borderBottom: '1px solid #f3f4f6' }}>
                        {row.title || row.chnlNm}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{
                          display: 'inline-block',
                          background: '#ecfdf5',
                          color: '#059669',
                          fontWeight: 600,
                          fontSize: '12px',
                          borderRadius: '20px',
                          padding: '3px 10px',
                        }}>
                          {row.subscriberCount ? row.subscriberCount.toLocaleString() + '명' : '-'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#4b5563', lineHeight: 1.6, borderBottom: '1px solid #f3f4f6', fontSize: '12px' }}>
                        {row.chnlDesc}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                        {row.customUrl ? (
                          <a
                            href={`https://www.youtube.com/${row.customUrl}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: '12px', color: '#667eea', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}
                          >
                            {`https://www.youtube.com/${row.customUrl}`}
                          </a>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <DataTable
        columns={columns}
        data={data}
        noDataComponent={
          loading ? <>데이터를 불러오는 중...</> : <>조회결과가 없습니다.</>
        }
        progressPending={loading}
        progressComponent={<div>데이터 로딩 중...</div>}
      />
    </>
  )
}

export default Youtube
